<script setup lang="ts">
import { useConfigurator } from '@/composables/configurator/useConfigurator'
import { useConfiguratorApi } from '@/composables/api/useConfiguratorApi'
import { useNativeWebComponentEvents } from '@/composables/webcomponents/useNativeWebComponentEvents'
import { useValidations } from '@/composables/api/useValidations'
import { useToast } from '@ai/ui-components/toast'
import LiveTestModal from '@/components/BusinessLogic/Modal/LiveTestModal.vue'
import VariableDictionaryPanel from '@/components/BusinessLogic/Variables/VariableDictionaryPanel.vue'
import { useLiveConfigurator } from '@/composables/configurator/useLiveConfigurator'
import { WebComponentTags } from '@/enums/WebComponents'
import { useAlerts } from '@/composables/alert/useAlerts'
import { alertEnum } from '@/enums/AlertEnum'
import { useKnowledgeApi } from '@/composables/api/useKnowledgeApi.ts'
import { useRoute, useRouter } from 'vue-router'
import { useFeatureFlags } from '@/composables/env/useFeatureFlags'
import { useSaveConfigurationMutation } from '@/composables/queries/configurations/useSaveConfigurationMutation'
import { saveDraftConfiguration } from '@/composables/livetest/useSaveDraftConfiguration'
import { useAnalytics } from '@/composables/analytics/useAnalytics'

const toast = useToast()
const { showToolsTab } = useFeatureFlags()
const { alerts } = useAlerts()
const { fetchDocumentsSummary, documentOptions } = useKnowledgeApi()

watch(
  alerts,
  (newAlerts) => {
    if (!newAlerts.length) return
    const last = newAlerts.at(-1)

    switch (last?.type) {
      case alertEnum.Success:
        toast.success(last.message)
        break
      case alertEnum.Danger:
        toast.error(last.message, { title: last.options?.title ?? t('error_occurred') })
        break
    }
  },
  { deep: true },
)
interface Props {
  id?: string | null
  backendUrl?: string
  duplicate?: boolean
}

const { id, duplicate } = defineProps<Props>()
const route = useRoute()
const router = useRouter()

const TAB_ROUTES = computed(() => [
  'general-information',
  'conversational-flow',
  'intent-recognition',
  'knowledge',
  'call-limits',
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  ...(showToolsTab ? ['tools'] : []),
  'experimental',
])

const selectedTab = ref(0)

watch(
  () => route.path,
  () => {
    const last = route.path.split('/').at(-1) ?? ''
    const index = TAB_ROUTES.value.indexOf(last)
    selectedTab.value = index === -1 ? 0 : index
  },
  { immediate: true },
)

const onTabChange = (index: number) => {
  selectedTab.value = index
  const locale = route.params.locale as string
  const id = route.params.id as string | undefined
  const base = id ? `/${locale}/configurator/${id}` : `/${locale}/configurator`
  void router.push(`${base}/${TAB_ROUTES.value[index]}`)
}

const {
  generalInformation,
  knowledge,
  experimental,
  payload,
  resetConfiguration,
  isNewConfiguration,
  saveInitialConfiguration,
  configurationId,
  setCustomerIntents,
  setDuplicated,
  loadConfigFromStorage,
  isCancelModalOpen,
  pendingNavigationTo,
  closeCancelModal,
} = useConfigurator()

setDuplicated(duplicate)

const { getConfiguration } = useConfiguratorApi()
const { getFullDispatchMetadata } = useLiveConfigurator()
const { t, locale: i18nLocale } = useI18n({ useScope: 'global' })

const { mutateAsync: saveConfiguration, isPending: isSaving } = useSaveConfigurationMutation()
const { track } = useAnalytics()

// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-redundant-type-constituents
const liveTestModalRef = ref<InstanceType<typeof LiveTestModal> | null>(null)
const liveTestConfigId = ref<string | undefined>(undefined)
// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-redundant-type-constituents
const varDictRef = ref<InstanceType<typeof VariableDictionaryPanel> | null>(null)

const openLiveTest = async () => {
  track('Live Test Clicked', {})
  markTriedSave()
  await scrollToFirstError()

  try {
    liveTestConfigId.value = await saveDraftConfiguration(getFullDispatchMetadata())
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    liveTestModalRef.value?.openPanel(liveTestConfigId.value)
  } catch {
    // Error handled by fetchApi alert system (same as create)
  }
}

const isLoading = ref(true)

onBeforeMount(async () => {
  setCustomerIntents([])

  // Preserve edit mode when restoring an existing configuration from session storage.
  if (id && !duplicate) {
    configurationId.value = id
  }

  // Fetch documents summary without blocking
  void fetchDocumentsSummary().then(() => {
    // Only auto-select all documents for NEW configs (not edit/duplicate)
    if (!id && !knowledge.value.document_ids?.length) {
      knowledge.value.document_ids = documentOptions.value.map((opt) => opt.value)
    }
  })

  if (loadConfigFromStorage(id ?? null)) {
    // Restored unsaved changes from sessionStorage (survives refresh)
  } else if (id || (id && duplicate)) {
    await getConfiguration(id, duplicate)
  }

  if (!id && !knowledge.value.custom_output) {
    knowledge.value.custom_output = 'ANSWER_FROM_KB'
  }
  if (!id && !knowledge.value.custom_output_unsuccessful) {
    knowledge.value.custom_output_unsuccessful = 'FAIL_FROM_KB'
  }

  isLoading.value = false
})

const nativeWebComponent = document.querySelector(WebComponentTags.AiVirtualAgentConfigurator)
const { emitCustomEvent } = useNativeWebComponentEvents(
  nativeWebComponent instanceof HTMLElement ? nativeWebComponent : undefined,
)

const { markTriedSave, resetErrors, scrollToFirstError } = useValidations()

const onSave = async () => {
  markTriedSave()
  await scrollToFirstError()

  const body = getFullDispatchMetadata()
  const isCreating = isNewConfiguration.value
  const id = isCreating ? null : configurationId.value
  const agentName = body.settings.general_information.agent_name

  try {
    const result = await saveConfiguration({ id, body })

    if (nativeWebComponent) {
      emitCustomEvent('configuration-submitted', {
        id: result.data.id,
      })
    } else {
      await router.push(`/${i18nLocale.value}/virtual-agents`)
    }

    toast.success(t(isCreating ? 'item_was_created' : 'item_was_updated', [agentName]))
  } catch {
    // Error display is handled by fetchApi/router consumers.
  }
}
const emit = defineEmits<(e: 'configuration-cancelled') => void>()
const onCancel = () => {
  const targetPath = pendingNavigationTo.value
  emit('configuration-cancelled')
  emitCustomEvent('configuration-cancelled', {})
  resetErrors()
  resetConfiguration()
  closeCancelModal()
  if (targetPath) {
    void router.push(targetPath)
  }
  return true
}

onMounted(() => {
  if (import.meta.env.DEV) {
    console.log('[Diabolocom Virtual Agent Configurator] version:', __APP_VERSION__)
  }
  if (!id) {
    configurationId.value = null
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    saveInitialConfiguration(JSON.parse(JSON.stringify(payload.value)))
  }
})

onUnmounted(() => {
  resetErrors()
  resetConfiguration()
})

const configuratorTabs = computed(() => {
  return [
    { label: t('general_information'), disabled: false },
    { label: t('conversational_flow'), disabled: false },
    {
      label: t('intent_recognition'),
      disabled: !experimental.value.tools.qualify_call && !experimental.value.tools.step_validation,
    },
    { label: t('knowledge'), disabled: !experimental.value.tools.answer_with_rag },
    { label: t('call_limits'), disabled: false },
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    ...(showToolsTab ? [{ label: t('tools.title'), disabled: false }] : []),
    { label: t('experimental'), disabled: false },
  ]
})

watch(
  [() => route.path, isLoading],
  () => {
    if (isLoading.value) return
    const last = route.path.split('/').at(-1) ?? ''
    const tabIndex = TAB_ROUTES.value.indexOf(last)
    if (tabIndex === -1) return
    const tab = configuratorTabs.value[tabIndex]
    if (tab.disabled) {
      const locale = route.params.locale as string
      const id = route.params.id as string | undefined
      const base = id ? `/${locale}/configurator/${id}` : `/${locale}/configurator`
      void router.replace(`${base}/general-information`)
    }
  },
  { immediate: true },
)
</script>
<template>
  <div class="h-full flex flex-col">
    <div v-if="isLoading" class="flex-1 flex items-center justify-center">
      <OSpinner size="L" />
    </div>

    <main v-else class="flex-1 min-h-0 flex flex-col">
      <OTabs
        class="flex-1 min-h-0"
        :model-value="selectedTab"
        @update:model-value="onTabChange"
        :tabs="configuratorTabs"
        data-testid="configurator-tabs"
      >
        <div class="flex-1 min-h-0 pt-4 pb-[70px] overflow-y-auto pr-10">
          <RouterView v-slot="slotProps">
            <KeepAlive v-if="slotProps?.Component">
              <component :is="slotProps.Component" />
            </KeepAlive>
          </RouterView>
        </div>
      </OTabs>
    </main>
    <footer
      v-if="!isLoading"
      class="fixed inset-x-0 bottom-0 z-10 bg-white border-t border-gray-200 ml-[260px]"
    >
      <div class="mx-auto px-4 py-[8px]">
        <div class="flex w-full justify-between px-6 lg:px-24">
          <div class="flex items-center space-x-3">
            <OButton :label="t('live_test')" @click="openLiveTest" dataTestid="live-test" />
            <OButton
              variant="secondary"
              :label="t('variables.title')"
              @click="varDictRef?.openPanel()"
              data-testid="variable-dictionary"
            />
          </div>

          <div class="flex items-center space-x-3">
            <CancelConfigModal
              v-model:open="isCancelModalOpen"
              :primary-action-promise="onCancel"
              :is-editing="!isNewConfiguration"
            >
              <template #default="{ handleClick }">
                <OButton
                  :label="t('cancel')"
                  variant="secondary"
                  color="neutral"
                  @click="handleClick"
                  data-testid="cancel-button"
                />
              </template>
            </CancelConfigModal>

            <OButton
              :label="isNewConfiguration ? t('create') : t('update')"
              :loading="isSaving"
              @click="onSave"
              loading-title="Loading"
              data-testid="save-button"
              backgroundColor="success"
            />
          </div>
        </div>
      </div>
    </footer>

    <LiveTestModal
      ref="liveTestModalRef"
      :config-id="liveTestConfigId"
      :config-name="generalInformation.agent_name"
    />
    <VariableDictionaryPanel ref="varDictRef" />
  </div>
</template>
